// Recurring breakdown modal — combines installments + fixed monthly commitments.
import { X, Repeat } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useInstallments } from "@/lib/useInstallmentsCache";
import { useCommitments } from "@/lib/useCommitmentsCache";
import { StatusPill, installmentStatus } from "@/components/dashboard/StatusPill";
import { InstallmentPayAccountModal } from "@/components/installments/InstallmentPayAccountModal";
import { useRecurringPay } from "@/lib/useRecurringPay";

const fmt = (n) => Math.round(Number(n || 0)).toLocaleString();
const PAY_BTN = "px-3 py-1.5 rounded-lg neu-fill-blue text-white text-[10px] font-bold active:scale-95 shrink-0";

export function CounterDetailsRecurring({ onClose }) {
  const { lang } = useI18n();
  const { user } = useAuth();
  const inst = useInstallments();
  const com = useCommitments();
  const ccy = user?.currency || "AED";
  const AR = lang === "ar";
  const active = (inst.active || []);
  const commit = (com.items || []).filter((c) => c.active !== false);
  const total = (inst.total_monthly || 0) + (com.total_monthly || 0);
  const { pay, setPay, submit, busy } = useRecurringPay(AR);
  const payLabel = AR ? "دفع" : "Pay";

  return (
    <div data-testid="counter-details-modal" dir={AR ? "rtl" : "ltr"} className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm grid place-items-end sm:place-items-center p-3" onClick={onClose}>
      <div className="w-full sm:max-w-md neu p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-extrabold neu-accent-blue flex items-center gap-2"><Repeat className="w-5 h-5" />{AR ? "الالتزامات الشهرية" : "Monthly recurring"}</h3>
          <button onClick={onClose} className="w-9 h-9 rounded-full neu-sm grid place-items-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-3 rounded-2xl neu-inset mb-3 flex items-center justify-between">
          <span className="text-sm font-bold">{AR ? "الإجمالي الشهري" : "Total monthly"}</span>
          <span className="font-display font-extrabold text-lg tabular-nums neu-accent-blue">{fmt(total)} {ccy}</span>
        </div>
        {active.length > 0 && <p className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-1.5 mt-2">{AR ? `الأقساط (${active.length})` : `Installments (${active.length})`}</p>}
        <div className="space-y-1.5">
          {active.map((r) => {
            const done = (r.paid_count || 0) >= (r.total_installments || 0);
            return (
              <div key={r.id} className="flex items-center gap-2 p-2.5 rounded-xl neu-sm text-sm" data-testid={`rec-inst-row-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold">{r.name}</p>
                  <p className="text-[10px] text-slate-400">{r.paid_count || 0}/{r.total_installments} · {r.next_due}</p>
                </div>
                <StatusPill status={installmentStatus(r)} lang={lang} testId={`rec-inst-status-${r.id}`} />
                {!done && <button data-testid={`rec-inst-pay-${r.id}`} onClick={() => setPay({ id: r.id, kind: "inst", amount: r.amount })} className={PAY_BTN}>{payLabel}</button>}
                <span className="tabular-nums font-bold shrink-0">{fmt(r.amount)} {r.currency || ccy}</span>
              </div>
            );
          })}
        </div>
        {commit.length > 0 && <p className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-1.5 mt-4">{AR ? `الالتزامات الثابتة (${commit.length})` : `Fixed commitments (${commit.length})`}</p>}
        <div className="space-y-1.5">
          {commit.map((r) => (
            <div key={r.id} className="flex items-center gap-2 p-2.5 rounded-xl neu-sm text-sm" data-testid={`rec-com-row-${r.id}`}>
              <p className="truncate font-semibold min-w-0 flex-1">{r.name}</p>
              <StatusPill status={r.status || "upcoming"} lang={lang} testId={`rec-com-status-${r.id}`} />
              {r.status !== "paid" && <button data-testid={`rec-com-pay-${r.id}`} onClick={() => setPay({ id: r.id, kind: "com", amount: r.amount })} className={PAY_BTN}>{payLabel}</button>}
              <span className="tabular-nums font-bold shrink-0">{fmt(r.amount)} {r.currency || ccy}</span>
            </div>
          ))}
        </div>
        {active.length === 0 && commit.length === 0 && (
          <p className="text-sm text-slate-500 py-6 text-center">{AR ? "لا التزامات نشطة" : "No active commitments"}</p>
        )}
      </div>
      <InstallmentPayAccountModal open={!!pay} amount={pay?.amount || 0} busy={busy}
        onConfirm={submit} onClose={() => setPay(null)} />
    </div>
  );
}
